import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Modal, Button, OverlayTrigger, Popover, Tooltip, Spinner, Form} from "react-bootstrap";
import { CheckCircle, AlertTriangle, File} from "react-feather";
import NotyfContext from "../contexts/NotyfContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import { caseInsensitiveSort } from "./reactTableUtils";
import getActivityDateTimeDisplay from "../utils/datetime"


const YesAnswersModal = ({ show, onClose, yesAnswers, name, completedDate}) => {
  const display = yesAnswers.map((answer) => {
    return (
      <tr key={answer.displayOrder}>
        <td>
          <b>{answer.displayOrder}. {answer.question}</b>
          <br />
          {answer.answerText}
          <br /><br />
        </td>
      </tr>
    );
  });
  
  return (
    <>
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
            <Modal.Title>
            {name} | {getActivityDateTimeDisplay(completedDate)}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table className="table-borderless table-sm">
            <tbody>
            {display}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
          <Button onClick={onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}


const Disclosure = ({ id, name, completedDate, sentToProvider, hasYesAnswers }) => {
  const [isExporting, setIsExporting] = useState(false);

  const notyf = useContext(NotyfContext);

  const handleExportClicked = (e) => {
    if (isExporting) {
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    e.stopPropagation();
    setIsExporting(true);
    authenticatedFetch({
      method: "GET",
      path:  "report/disclosures/" + id,
      additionalHeaders: {
        "X-Timezone": timezone,
      },
      successHandler: function(result) {
        window.open(result.url, "_blank", "noopener,noreferrer");
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  return (
    <>
    <td>
      {name}
    </td>
    <td>
    {getActivityDateTimeDisplay(completedDate)}
    </td>
    <td>
      {sentToProvider ? "Provider" : "Admin"}
    </td>
    <td>
      {hasYesAnswers ?  <AlertTriangle className="text-danger" /> : 
        <>
        <OverlayTrigger
          triggers="[hover, focus]"
          placement="bottom"
          overlay={<Popover><Popover.Body>All answers were '<b>no</b>'!</Popover.Body></Popover>}
        >
          <CheckCircle className="text-success" />
        </OverlayTrigger>
        </>
      }
    </td>
    <td onClick={(e) => {
          handleExportClicked(e);
        }}>
      { isExporting ? 
        <Spinner animation="border" size="sm" variant="primary" style={{marginLeft: '4px'}}/>
        :
        <Form.Label onClick={(e) => {handleExportClicked(e)}} style={{cursor: "pointer", display: "inline", fontSize: "0.8em", fontWeight: "normal", color: '#2B7A78', marginTop: "7px", marginRight: "3px"}}>
          <FontAwesomeIcon icon={faFilePdf} size="lg" style={{color: '#2B7A78'}}/>
        </Form.Label>
      }
    </td>
  </>
  )
}

const DisclosureRow = ({ row }) => {
  const [showYesAnswersModal, setShowYesAnswersModal] = useState(false);
  const { id, yesAnswers, name, completedDate, sentToProvider } = row.values;
 
  let hasYesAnswers = row.values.yesAnswers && row.values.yesAnswers.length > 0;

  return (
    <>
    <tr style={{ cursor: hasYesAnswers ? "pointer" : "default" }} onClick={hasYesAnswers ? () => { setShowYesAnswersModal(true) } : undefined}>
      <Disclosure key={id} id={id} name={name ? name : ""} completedDate={completedDate} sentToProvider={sentToProvider} hasYesAnswers={hasYesAnswers} />
    </tr>
    {hasYesAnswers && <YesAnswersModal key={id} name={name} completedDate={completedDate} yesAnswers={yesAnswers} show={showYesAnswersModal} onClose={() => { setShowYesAnswersModal(false) }} />}
    </>
  );
};

const DisclosuresTable = ({ list, isLoaded, error }) => {
  const data = list;

  const columns = React.useMemo(
    () => [
      {
        accessor: "id",
        key: "id",
      },
      {
        accessor: "yesAnswers",
        key: "yesAnswers",
      },
      {
        Header: "Type",
        accessor: "name",
        key: "name",
      },
      {
        Header: "Date",
        accessor: "completedDate",
        key: "completedDate",
      },
      {
        Header: "Completed by",
        accessor: "sentToProvider",
        key: "sentToProvider",
      },
      {
        Header: "Status",
        accessor: "status",
        key: "status",
      },
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Export</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "export",
        key: "export",
      }
    ],
  []
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id', 'yesAnswers'],
        sortBy: [
          {
            id: 'completedDate',
            desc: true
          }
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  return (
    <Table className="table-sm" striped bordered hover {...getTableProps()}>
    <thead>
      {headerGroups.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => {
            const { key, ...restHeaderProps } = column.id=== "export" ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
            return (
              <th {...restHeaderProps} key={key}>
                {column.render("Header")}
                {column.isSorted ? (
                    column.isSortedDesc ? (
                      <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                    ) : (
                      <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                    )
                  ) : (
                    <FontAwesomeIcon icon={faSort} className="ms-2" />
                )}
              </th>
            )
          }
          )}
        </tr>
      ))}
    </thead>
    <tbody {...getTableBodyProps()}>
      {
      error ? (<tr><td colSpan={5}>Something went wrong; please reload the page...</td></tr>) : ( 
        !isLoaded ? (<tr><td colSpan={5}>Loading...</td></tr>) : (
          rows.map((row) => {
          prepareRow(row);
          const { key, ...restRowProps } = row.getRowProps();

          return (
           <DisclosureRow key={key} row={row} {...restRowProps} />
          );
        })))
      }
    </tbody>
  </Table>
  )
}

const Disclosures = ({providerId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    refreshList();
  }, [providerId])

  function refreshList()
  {
    authenticatedFetch({
      path: "surveyresponse/disclosures/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  let list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, name: item.name, completedDate: item.completedDate, sentToProvider: item.sentToProvider, yesAnswers: item.yesAnswers}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Disclosures
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <DisclosuresTable list={list} providerId={providerId} error={error} isLoaded={isLoaded}/>
      </Card.Body>
    </Card>
  )
}

export default Disclosures;
